import { useCallback, useState } from 'react';
import { Form, message, Spin } from 'antd';
import { Banner } from 'components/ui/Banner';
import PageMeta from 'components/PageMeta/PageMeta';
import RadioCards from 'components/RadioCards/RadioCards';
import { familyService } from 'services';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { FAMILY_SPOUSE_UNEMPLOYMENT_REASON } from './constants';
import { getOtherAdultFirstName } from '../../utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';

export function FamilyAppSpouseUnemploymentReason({
  application,
  setApplication,
  household,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);

  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const onSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        // Use getNextStep to determine the next step
        const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.SPOUSE_UNEMPLOYMENT_REASON, _newApplication, stepConfig);
        setStep({
          applicationId: _newApplication?.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        const _spouse = application?.additionalParents?.[0];
        const reasonChanged = _spouse?.reasonForUnemployment !== values.reasonForUnemployment;

        const updatedSpouse = await familyService.addUpdateAdult(
          household?.id,
          application?.additionalParents?.[0]?.id ?? null,
          {
            ...application?.additionalParents?.[0],
            reasonForUnemployment: values.reasonForUnemployment,
            // FAMILY-APPS__DATA_CLEANING => Clear unemploymentDetails when reason changes
            // Each reason (JobSearch, MedicalReason, Other) has different certification requirements
            ...(reasonChanged && {
              unemploymentDetails: null,
              unemploymentCertificationComplete: null,
            }),
          },
        );

        const _newApplication = await familyService.saveApplication({
          ...application,
          additionalParents: [updatedSpouse],
        });

        setApplication(_newApplication);
        _goToNextStep(_newApplication);
      } catch (error) {
        message.error('Something went wrong');
        newrelic.noticeError(error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, household?.id, readOnly, setApplication, setStep, stepConfig],
  );

  // Set initial values
  useEffect(() => {
    if (application?.additionalParents?.[0]) {
      form.setFieldsValue({
        reasonForUnemployment: application?.additionalParents?.[0]?.reasonForUnemployment,
      });
    }
  }, [application, form]);

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={false}>
      <PageMeta title="Spouse Current Circumstances" />

      <Form form={form} layout="vertical" onFinish={onSubmit} disabled={readOnly}>
        <div className="p-7 pt-0">
          <Banner
            question={`Tell us more about ${getOtherAdultFirstName(application?.additionalParents?.[0])}'s situation`}
            guidance={`Select the option that best describes ${getOtherAdultFirstName(
              application?.additionalParents?.[0],
            )}'s current circumstances`}
            className="my-12"
          />

          <Form.Item name="reasonForUnemployment" rules={[{ required: true, message: 'Please select a reason' }]}>
            <RadioCards
              options={[
                {
                  label: 'Looking for work',
                  value: FAMILY_SPOUSE_UNEMPLOYMENT_REASON.JobSearch,
                },
                {
                  label: 'Managing a medical condition',
                  value: FAMILY_SPOUSE_UNEMPLOYMENT_REASON.MedicalReason,
                },
              ]}
            />
          </Form.Item>
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            disabled: submitting,
            onClick: () => {
              // Use getPreviousStep to determine the previous step
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.SPOUSE_UNEMPLOYMENT_REASON,
                application,
                stepConfig,
              );

              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
          }}
          nextButtonProps={{
            loading: submitting,
          }}
        />
      </Form>
    </Spin>
  );
}

export default FamilyAppSpouseUnemploymentReason;
